// Captures everything we know about an HTTP request at the moment it's
// fired and at the moment it completes, so that error logs / user-facing
// diagnostics carry enough context to triage without a reproduction.
//
// Crucially this captures *request-side* signals (payload size, target,
// caller-attached scene context, timing) that survive even when CORS
// strips the response — the case where browser-side error data is the
// only thing we'll ever get.

let _userEmail = null;
export function setRequestEnvelopeUserEmail(email) {
  _userEmail = email || null;
}

function generateRequestId() {
  // 8 hex chars — enough to disambiguate within a session; short enough to
  // paste into bug reports without making them look like noise.
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const buf = new Uint8Array(4);
    crypto.getRandomValues(buf);
    return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return Math.random().toString(16).slice(2, 10);
}

function measurePayloadBytes(body) {
  if (body === null || body === undefined) return 0;
  if (typeof body === "string") return new Blob([body]).size;
  if (body instanceof Blob) return body.size;
  if (body instanceof ArrayBuffer) return body.byteLength;
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    // Sum the byte size of every entry. File/Blob expose `.size` in O(1) (no
    // byte walking) and string fields are tiny, so this is cheap — the old
    // "expensive, return 0" rationale was wrong. For uploads the payload size
    // is the single most useful diagnostic, and reporting 0 made every upload
    // bug report's size field useless (see /api/v3/uploads incident). This
    // ignores multipart framing overhead, so it's a close lower bound, not exact.
    let total = 0;
    for (const value of body.values()) {
      if (typeof value === "string") total += new Blob([value]).size;
      else if (value && typeof value.size === "number") total += value.size;
    }
    return total;
  }
  return 0;
}

export function createRequestEnvelope({url, method, body}) {
  return {
    requestId: generateRequestId(),
    url,
    method: method || "GET",
    payloadBytes: measurePayloadBytes(body),
    userEmail: _userEmail,
    ts: new Date().toISOString(),
    t0: Date.now(),
  };
}

export function finalizeRequestEnvelope(envelope, {response, error} = {}) {
  envelope.elapsedMs = Date.now() - envelope.t0;
  if (response) {
    envelope.status = response.status;
    envelope.statusText = response.statusText;
  }
  if (error) {
    envelope.error = {
      name: error.name,
      message: error.message,
      // TypeError + "Failed to fetch" is the canonical CORS / network-stripped
      // signature. Flag it explicitly so consumers don't have to re-detect.
      isLikelyCors:
        error.name === "TypeError" && /failed to fetch|networkerror/i.test(error.message),
    };
  }
  return envelope;
}

// Strip the internal `t0` timestamp before serializing for human consumption.
// Everything else is intentionally exposed: the user pasting this into a bug
// report wants ops to see status, timing, payload size, target URL.
export function serializeEnvelope(envelope, extraContext = {}) {
  const {t0: _t0, ...rest} = envelope;
  return {...rest, ...extraContext};
}
